import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
    ContentCopyOutlinedIcon,
    EditOutlinedIcon,
    RemoveCircleOutlineIcon,
    ScheduleOutlinedIcon,
    VisibilityOutlinedIcon,
} from 'tuui';
import { useNavigate } from 'hooks';
import { useRealtime } from 'modules/campaigns/hooks';
import { useGridContext } from 'modules/campaigns/modules/campaigns-reports/components/GridContextProvider/GridContextProvider';
import {
    isAppInstall,
    isBrandsVideoCreative,
    isCreativeLibraryEnabled,
    isCreativeResubmittable,
    isDeleteCreativeAllowed,
    isDuplicateCreativeAllowed,
    isEditCreativeAllowed,
    isLibraryCreative,
    isMotionAd,
    isPreviewCreativeAllowed,
    isRssChild,
    isAdRemoved,
} from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import { isCarouselCreative } from 'modules/campaigns/modules/creative-creator/components/CarouselCardList/utils/isCarouselCreative';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication/hooks';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { useModal } from 'modules/taboola-common-frontend-modules/modals';
import ActionsCellRenderer from './ActionsCellRenderer';

const ACTIONS = {
    EDIT: 'edit',
    DUPLICATE: 'duplicate',
    PREVIEW: 'preview',
    REMOVE: 'remove',
};

const AD_TYPE = {
    MOTION_AD: 'video',
    APP_INSTALL: 'appinstall',
    RSS: 'rss',
    UNRESUBMITTABLE: 'unresubmittable',
    HIERARCHY: 'hierarchy',
    GENERAL: 'general',
};

const getTooltipMessage = (data, action, isMessageIdExists, reportId) => {
    const motionAdMessageId = isMotionAd(data) ? AD_TYPE.MOTION_AD : null;
    const appInstallMessageId = isAppInstall(data) ? AD_TYPE.APP_INSTALL : null;
    const rssMessageId = isRssChild(data) ? AD_TYPE.RSS : null;
    const hierarchyMessageId = isCarouselCreative(data) ? AD_TYPE.HIERARCHY : null;
    const unresubmittableMessageId =
        !isCreativeResubmittable(data) && action === ACTIONS.EDIT ? AD_TYPE.UNRESUBMITTABLE : null;
    const domain =
        hierarchyMessageId ||
        appInstallMessageId ||
        motionAdMessageId ||
        rssMessageId ||
        unresubmittableMessageId ||
        AD_TYPE.GENERAL;
    const messageId = `app.campaigns.creativeReports.actions.${action}.${reportId}.${domain}`;
    if (isMessageIdExists(messageId)) {
        return <FormattedMessage id={messageId} />;
    }
    return null;
};

const getTooltipProps = (data, action) => {
    if (!isCreativeResubmittable(data) && action === ACTIONS.EDIT) {
        return { interactive: true };
    }
    return null;
};

const getRemoveModalMessages = title => ({
    title: (
        <FormattedMessage
            id="creative.remove.header"
            tagName="h2"
            defaultMessage="{selectedCount, plural, one{Remove Ad?} other{Remove Ads?}}"
            values={{ selectedCount: 1 }}
        />
    ),
    content: (
        <FormattedMessage
            id="creative.modal.content.remove"
            defaultMessage="You are about to remove {title} from the campaign. Once an ad is removed, it can never run again. Instead of removing, you can pause the ad. Do you wish to proceed?"
            values={{ title }}
        />
    ),
    formProps: { submitButtonText: <FormattedMessage id="app.actionButtons.REMOVE" defaultMessage="REMOVE" /> },
});

const CreativeActionsCellRenderer = ({
    data,
    onEdit,
    onPreview,
    onDelete,
    onDuplicate,
    onScheduler,
    context,
    reportId = 'creative',
    ...rest
}) => {
    const { isMessageIdExists } = useIntl();
    const { open: openModal } = useModal();
    const dispatch = useDispatch();
    const hasEditPermission = usePermissions('CREATIVE_EDIT');
    const { isGridEditDisabled } = useGridContext();
    const navigate = useNavigate();

    const isCreativeDuplicatorDrawerEnabled = useConfigMatch({
        [FEATURE_FLAGS.CREATIVE_DUPLICATE_ENABLED]: 'true',
    });

    const isCreativeDuplicatorVideoScDrawerEnabled = useConfigMatch({
        [FEATURE_FLAGS.CREATIVE_EDIT_VIDEO_DUPLICATE_ENABLED]: 'true',
    });

    const { isRealtimeActive } = useRealtime();

    const removeHandler = async data => {
        const { title } = data;
        const result = await openModal(getRemoveModalMessages(title));

        if (result) {
            dispatch(onDelete(data, context));
        }
    };

    const buttonConfigs = [];

    const isEditButtonDisabled =
        !isEditCreativeAllowed(data) ||
        !hasEditPermission ||
        isGridEditDisabled ||
        (isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig));

    const isRemoveButtonDisabled =
        isCarouselCreative(data) ||
        !isDeleteCreativeAllowed(data) ||
        !hasEditPermission ||
        (isLibraryCreative({ data }) &&
            isCreativeLibraryEnabled(context?.selectedAccountConfig) &&
            isAdRemoved({ data }));

    const isDuplicateButtonDisabled =
        !isDuplicateCreativeAllowed(data) ||
        (isMotionAd(data) && !isCreativeDuplicatorVideoScDrawerEnabled) ||
        isBrandsVideoCreative(data) ||
        !hasEditPermission ||
        isGridEditDisabled ||
        isAppInstall(data) ||
        isRealtimeActive ||
        (isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig));

    const isPreviewButtonDisabled =
        !isPreviewCreativeAllowed(data) ||
        (isLibraryCreative({ data }) &&
            isCreativeLibraryEnabled(context?.selectedAccountConfig) &&
            isAdRemoved({ data }));

    buttonConfigs.push({
        onClick: () => onEdit({ data, context: { ...context, navigate } }),
        isDisabled: isEditButtonDisabled,
        dataMetricComponent: 'EditActionButton',
        icon: <EditOutlinedIcon />,
        iconLabel: 'edit',
        tooltipContent: getTooltipMessage(data, ACTIONS.EDIT, isMessageIdExists, reportId),
        tooltipProps: getTooltipProps(data, ACTIONS.EDIT),
    });

    if (isCreativeDuplicatorDrawerEnabled) {
        buttonConfigs.push({
            onClick: () => dispatch(onDuplicate(data, navigate)),
            isDisabled: isDuplicateButtonDisabled,
            dataMetricComponent: 'DuplicateActionButton',
            icon: <ContentCopyOutlinedIcon />,
            iconLabel: 'duplicate',
            tooltipContent: isRealtimeActive ? (
                <FormattedMessage
                    id="app.campaigns.creativeReports.actions.duplicate.creative.realtime"
                    defaultMessage="Duplicating ads is not supported yet in the “real-time” mode."
                />
            ) : (
                getTooltipMessage(data, ACTIONS.DUPLICATE, isMessageIdExists, reportId)
            ),
        });
    }

    buttonConfigs.push({
        onClick: () => dispatch(onPreview(data, navigate)),
        isDisabled: isPreviewButtonDisabled,
        dataMetricComponent: 'PreviewActionButton',
        icon: <VisibilityOutlinedIcon />,
        iconLabel: 'preview',
        permissionsGuardProps: {
            permissions: 'CREATIVE_PREVIEW_VIEW',
        },
        tooltipContent: getTooltipMessage(data, ACTIONS.PREVIEW, isMessageIdExists, reportId),
    });
    if (!isBrandsVideoCreative(data)) {
        buttonConfigs.push({
            onClick: () => onScheduler({ data, context: { ...context, navigate } }),
            isDisabled: isEditButtonDisabled || isCarouselCreative(data),
            dataMetricComponent: 'SchedulerActionButton',
            icon: <ScheduleOutlinedIcon />,
            iconLabel: 'schedule',
            tooltipContent: (
                <FormattedMessage
                    id="app.campaigns.creativeReports.actions.scheduler.creative.general"
                    defaultMessage="Ad Schedule"
                />
            ),
        });
    }
    buttonConfigs.push({
        onKeyDown: e => e.preventDefault(),
        onClick: () => removeHandler(data),
        isDisabled: isRemoveButtonDisabled,
        dataMetricComponent: 'RemoveActionButton',
        icon: <RemoveCircleOutlineIcon />,
        iconLabel: 'remove',
        tooltipContent: getTooltipMessage(data, ACTIONS.REMOVE, isMessageIdExists, reportId),
    });

    return <ActionsCellRenderer data={data} buttonConfigs={buttonConfigs} context={context} {...rest} />;
};

CreativeActionsCellRenderer.propTypes = {
    /** row data which is injected by the grid */
    data: PropTypes.object,
    onDelete: PropTypes.func,
    onPreview: PropTypes.func,
    ...ActionsCellRenderer.propTypes,
};

export default CreativeActionsCellRenderer;
